import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "../FormField";
import { fmtDate } from "../../helpers";
import { Zap, Plus, Trash2, Pencil, Play, ScrollText } from "lucide-react";

const empty = { name: "", description: "", trigger: "manuale", actions: [], priority: 1, enabled: true, provider: "internal", webhook_url: "" };

export default function AutomationsTab({ apartmentId }) {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState({ triggers: [], actions: [] });
  const [form, setForm] = useState(null);
  const [logs, setLogs] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([
        api.get(`/automations`, { params: { apartment_id: apartmentId } }),
        api.get(`/automations/catalog`),
      ]);
      setItems(a.data); setCat(c.data);
    } catch { /* noop */ }
  }, [apartmentId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    try {
      if (form.id) await api.put(`/automations/${form.id}`, form);
      else await api.post(`/automations`, { ...form, apartment_id: apartmentId });
      toast.success("Salvato"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (aid) => { await api.delete(`/automations/${aid}`); load(); };
  const toggle = async (a) => { await api.put(`/automations/${a.id}`, { enabled: !a.enabled }); load(); };
  const test = async (a) => { const r = await api.post(`/automations/${a.id}/test`); toast.message(r.data.log.message); load(); };
  const showLogs = async (a) => { const r = await api.get(`/automations/${a.id}/logs`); setLogs({ name: a.name, items: r.data }); };
  const toggleAction = (act) => setForm((f) => ({ ...f, actions: f.actions.includes(act) ? f.actions.filter((x) => x !== act) : [...f.actions, act] }));

  return (
    <Card className="border-slate-200 p-5" data-testid="detail-automations">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-head font-semibold text-slate-900"><Zap size={18} /> Automation Center</h3>
        <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="auto-new" onClick={() => setForm(empty)}><Plus size={14} className="mr-1" />Nuova</Button>
      </div>
      <p className="mb-4 text-xs text-slate-400">Configura trigger, azioni e priorità. L'esecuzione reale verrà abilitata con le integrazioni. Usa "Test" per simulare.</p>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400">Nessuna automazione configurata.</p>}
        {items.map((a) => (
          <div key={a.id} data-testid={`auto-${a.id}`} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 font-medium text-slate-800">
                  {a.name}
                  {!a.apartment_id && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">GLOBALE</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.enabled ? "Attiva" : "Disattiva"}</span>
                </div>
                <div className="text-xs text-slate-400">Trigger: {a.trigger} · Azioni: {a.actions?.join(", ") || "—"} · Priorità {a.priority}
                  {a.last_run && <> · ultima esec. {fmtDate(a.last_run)} ({a.run_count})</>}
                  {a.last_error && <span className="text-rose-500"> · {a.last_error}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`auto-toggle-${a.id}`} />
                <Button size="sm" variant="ghost" className="text-emerald-600" data-testid={`auto-test-${a.id}`} onClick={() => test(a)}><Play size={13} /></Button>
                <Button size="sm" variant="ghost" data-testid={`auto-logs-${a.id}`} onClick={() => showLogs(a)}><ScrollText size={13} /></Button>
                {a.apartment_id === apartmentId && <Button size="sm" variant="ghost" data-testid={`auto-edit-${a.id}`} onClick={() => setForm(a)}><Pencil size={13} /></Button>}
                {a.apartment_id === apartmentId && <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`auto-del-${a.id}`} onClick={() => del(a.id)}><Trash2 size={13} /></Button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="auto-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Modifica automazione" : "Nuova automazione"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <Field label="Nome"><Input data-testid="auto-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="Descrizione"><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Trigger">
                  <Select value={form.trigger} onValueChange={(v) => setForm((f) => ({ ...f, trigger: v }))}>
                    <SelectTrigger data-testid="auto-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{cat.triggers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priorità"><Input type="number" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: +e.target.value }))} /></Field>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-slate-500">Azioni</div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.actions.map((act) => (
                    <button key={act} type="button" data-testid={`auto-action-${act}`} onClick={() => toggleAction(act)}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${form.actions.includes(act) ? "border-primary bg-primary text-primary-foreground" : "border-slate-200 text-slate-600 hover:border-primary"}`}>
                      {act}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Webhook / Make URL (opz.)"><Input value={form.webhook_url} onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))} placeholder="https://hook.make.com/..." /></Field>
              <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2">
                <span className="text-sm text-slate-600">Attiva</span>
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Annulla</Button>
                <Button className="bg-primary hover:bg-primary/90" data-testid="auto-save" onClick={save}>Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logs dialog */}
      <Dialog open={!!logs} onOpenChange={(o) => !o && setLogs(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="auto-logs-dialog">
          <DialogHeader><DialogTitle>Cronologia esecuzioni · {logs?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {logs?.items?.length === 0 && <p className="text-sm text-slate-400">Nessuna esecuzione.</p>}
            {logs?.items?.map((l) => (
              <div key={l.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                <div className="flex justify-between">
                  <span className={`text-xs font-semibold ${l.status === "error" ? "text-rose-600" : l.status === "success" ? "text-emerald-600" : "text-amber-600"}`}>{l.status.toUpperCase()}</span>
                  <span className="text-xs text-slate-400">{fmtDate(l.at)} · {l.triggered_by}</span>
                </div>
                <div className="text-slate-600">{l.message}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
